import { useState } from "react";
import { toast } from "sonner";
import { Link2, Share2 } from "lucide-react";
import { SITE_URL } from "./Seo";

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d={d} /></svg>
);
const WA = "M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .2-3.3-.7-2.8-1.1-4.5-3.9-4.7-4.1-.1-.2-1.1-1.5-1.1-2.8s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.4.6-.4.4c-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.7-.1l1.9.9c.3.1.5.2.5.3.1.2.1.7-.1 1.3Z";
const FB = "M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.5 1.6-1.5h1.6V4.4c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.4H7.6V14h2.7v8h3.2Z";
const X = "M17.8 3h3.1l-6.8 7.7L22 21h-6.2l-4.9-6.3L5.3 21H2.2l7.2-8.3L1.8 3h6.4l4.4 5.8L17.8 3Zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5Z";
const PIN = "M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.5 1.8-2.5.9 0 1.3.6 1.3 1.4 0 .9-.5 2.1-.8 3.3-.2 1 .5 1.8 1.5 1.8 1.8 0 3.1-1.9 3.1-4.6 0-2.4-1.7-4.1-4.2-4.1-2.9 0-4.5 2.1-4.5 4.4 0 .9.3 1.8.8 2.3.1.1.1.2.1.3l-.3 1.2c0 .2-.2.3-.4.2-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.8-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.2 6.4-5.4 6.4-1 0-2-.5-2.4-1.2l-.6 2.5c-.2.9-.9 2.1-1.3 2.8A10 10 0 1 0 12 2Z";
const LI = "M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9.5h4V21H3V9.5Zm6.5 0h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.2 0-2.9-1.8-2.9s-2 1.4-2 2.8V21h-4V9.5Z";

export const ShareBar = ({ path, title, text, image, testid = "share-bar" }) => {
  const [copied, setCopied] = useState(false);
  const url = `${SITE_URL}${path}`;
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text || title);
  const links = [
    ["whatsapp", "WhatsApp", WA, `https://wa.me/?text=${t}%20${u}`],
    ["facebook", "Facebook", FB, `https://www.facebook.com/sharer/sharer.php?u=${u}`],
    ["x", "X", X, `https://twitter.com/intent/tweet?text=${t}&url=${u}`],
    ["pinterest", "Pinterest", PIN, `https://pinterest.com/pin/create/button/?url=${u}&media=${encodeURIComponent(image || `${SITE_URL}/og-image.jpg`)}&description=${t}`],
    ["linkedin", "LinkedIn", LI, `https://www.linkedin.com/sharing/share-offsite/?url=${u}`],
  ];
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };
  const native = () => navigator.share({ title, text, url }).catch(() => {});
  const btn = "inline-grid h-10 w-10 shrink-0 align-middle place-items-center rounded-full border border-jute/80 bg-card/60 transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-walnut hover:bg-walnut hover:text-linen active:translate-y-0";
  return (
    <div data-testid={testid} className="flex flex-wrap items-center gap-2">
      <span className="mr-1 text-sm font-medium">Share</span>
      {links.map(([k, label, d, href]) => (
        <a key={k} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${label}`} data-testid={`${testid}-${k}`} className={btn}><Icon d={d} /></a>
      ))}
      <button onClick={copy} aria-label="Copy link" data-testid={`${testid}-copy`} className={btn}><Link2 className="h-4 w-4" /></button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button onClick={native} aria-label="More share options" data-testid={`${testid}-native`} className={`${btn} sm:hidden`}><Share2 className="h-4 w-4" /></button>
      )}
      {copied && <span className="sr-only">Copied</span>}
    </div>
  );
};
